import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import complianceReportsCustomizationService from '../../../services/complianceReportsCustomizationService';

const CostImpactAnalysisPanel = ({ reportId }) => {
  const [costAnalysis, setCostAnalysis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (reportId) {
      loadCostAnalysis();
    }
  }, [reportId]);

  const loadCostAnalysis = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await complianceReportsCustomizationService?.getCostImpactAnalysis(reportId);
      setCostAnalysis(data);
    } catch (err) {
      setError(err?.message || 'Failed to load cost analysis');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `K${parseFloat(amount || 0)?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  };

  const totalCost = costAnalysis?.reduce((sum, item) => sum + (parseFloat(item?.totalCostKina) || 0), 0);
  const totalBackpay = costAnalysis?.reduce((sum, item) => sum + (parseFloat(item?.backpayCostKina) || 0), 0);
  const totalPenalty = costAnalysis?.reduce((sum, item) => sum + (parseFloat(item?.penaltyCostKina) || 0), 0);
  const totalAdmin = costAnalysis?.reduce((sum, item) => sum + (parseFloat(item?.administrativeCostKina) || 0), 0);

  if (loading) {
    return (
      <div className="bg-card rounded-lg shadow-md p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/3"></div>
          <div className="grid grid-cols-4 gap-4">
            <div className="h-24 bg-muted rounded"></div>
            <div className="h-24 bg-muted rounded"></div>
            <div className="h-24 bg-muted rounded"></div>
            <div className="h-24 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card rounded-lg shadow-md p-6">
        <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-md flex items-start gap-2">
          <Icon name="AlertCircle" size={18} className="mt-0.5" />
          <span className="text-sm">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-md p-6 space-y-6">
      <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
        <Icon name="DollarSign" size={20} />
        Cost Impact Analysis (PNG Kina)
      </h3>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-error/10 border border-error rounded-lg p-4">
          <p className="text-xs text-error font-medium mb-1">Total Cost</p>
          <p className="text-2xl font-bold text-error">{formatCurrency(totalCost)}</p>
        </div>
        <div className="bg-warning/10 border border-warning rounded-lg p-4">
          <p className="text-xs text-warning font-medium mb-1">Backpay</p>
          <p className="text-2xl font-bold text-warning">{formatCurrency(totalBackpay)}</p>
        </div>
        <div className="bg-primary/10 border border-primary rounded-lg p-4">
          <p className="text-xs text-primary font-medium mb-1">Penalties</p>
          <p className="text-2xl font-bold text-primary">{formatCurrency(totalPenalty)}</p>
        </div>
        <div className="bg-muted border border-border rounded-lg p-4">
          <p className="text-xs text-muted-foreground font-medium mb-1">Administrative</p>
          <p className="text-2xl font-bold text-foreground">{formatCurrency(totalAdmin)}</p>
        </div>
      </div>

      {/* Detailed Breakdown */}
      {costAnalysis?.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="DollarSign" size={48} className="mx-auto mb-3 opacity-50" />
          <p className="text-sm">No cost analysis data available</p>
        </div>
      ) : (
        <div className="space-y-4">
          {costAnalysis?.map((item) => (
            <div key={item?.id} className="bg-muted/30 rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-foreground">{item?.costCategory}</h4>
                    {item?.department && (
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                        {item?.department}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{item?.analysisPeriod}</p>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-error">{formatCurrency(item?.totalCostKina)}</p>
                  <p className="text-xs text-muted-foreground">
                    {item?.affectedEmployees} employees ({formatCurrency(item?.costPerEmployeeKina)}/employee)
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-3">
                <div className="bg-card rounded p-2">
                  <p className="text-xs text-muted-foreground mb-1">Backpay</p>
                  <p className="text-sm font-medium text-foreground">{formatCurrency(item?.backpayCostKina)}</p>
                </div>
                <div className="bg-card rounded p-2">
                  <p className="text-xs text-muted-foreground mb-1">Penalties</p>
                  <p className="text-sm font-medium text-foreground">{formatCurrency(item?.penaltyCostKina)}</p>
                </div>
                <div className="bg-card rounded p-2">
                  <p className="text-xs text-muted-foreground mb-1">Administrative</p>
                  <p className="text-sm font-medium text-foreground">{formatCurrency(item?.administrativeCostKina)}</p>
                </div>
              </div>

              {item?.mitigationRecommendations?.length > 0 && (
                <div className="border-t border-border pt-3">
                  <p className="text-xs font-medium text-foreground mb-2">Mitigation Recommendations:</p>
                  <ul className="space-y-1">
                    {item?.mitigationRecommendations?.map((rec, index) => (
                      <li key={index} className="text-xs text-muted-foreground flex items-start gap-2">
                        <Icon name="CheckCircle" size={14} className="mt-0.5 text-success" />
                        <span>{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CostImpactAnalysisPanel;